import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Box,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import OrganizationIcon from "@mui/icons-material/AccountTree";
import UserIcon from "@mui/icons-material/Person";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import StorageIcon from "@mui/icons-material/Storage";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../services/auth/AuthContext";
import Logo from "../../assets/logo.svg";
import darklogo from "../../assets/spark_logo_dark.svg";

const drawerWidth = 240;

const sidebarItems = [
  // {
  //   label: "Dashboard",
  //   icon: <DashboardIcon fontSize="small" />,
  //   to: "/dashboard",
  // },
  {
    label: "Organisation",
    icon: <OrganizationIcon fontSize="small" />,
    to: "/organisation",
  },
  {
    label: "User",
    icon: <UserIcon fontSize="small" />,
    to: "/user",
  },
  // {
  //   label: "Master File",
  //   icon: <DescriptionIcon fontSize="small" />,
  //   to: "/master-file",
  // },
  {
    label: "Master Data",
    icon: <StorageIcon fontSize="small" />,
    to: "/master-data",
  },
  {
    label: "Role Permissions",
    icon: <ManageAccountsIcon fontSize="small" />,
    to: "/role-permission",
  },
];

interface SideBarProps {
  isOpen: boolean;
  closeSidebar: () => void;
}

const Sidebar: React.FC<SideBarProps> = ({ isOpen, closeSidebar }) => {
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  
  // Theme-aware color parameters
  const activeColor = isDarkMode ? "#818CF8" : "#3A2ADA";
  const activeBg = isDarkMode ? "rgba(129, 140, 248, 0.16)" : "rgba(58, 42, 218, 0.08)";
  const activeHoverBg = isDarkMode ? "rgba(129, 140, 248, 0.24)" : "rgba(58, 42, 218, 0.12)";
  const hoverBg = isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(58, 42, 218, 0.04)";
  const inactiveIconColor = isDarkMode ? "#94A3B8" : "#64748B";
  const inactiveTextColor = isDarkMode ? "#E2E8F0" : "#334155";

  // Determine if sidebar is temporary (mobile) or permanent (desktop)
  const isTemporary =
    typeof isOpen === "boolean" && typeof closeSidebar === "function";

  // Resolve role from possible shapes returned by auth
  const role =
    (user as any)?.role ??
    (user as any)?.data?.role ??
    (user as any)?.user?.role ??
    "";

  // Role-based sidebar visibility logic
  const getVisibleItems = () => {
    if (role === "Super Admin") {
      return sidebarItems;
    } else {
      return sidebarItems.filter((item) => item.label === "Organisation");
    }
  };
  
  const visibleItems = getVisibleItems();

  return (
    <Drawer
      variant={isTemporary ? "temporary" : "permanent"}
      open={isTemporary ? isOpen : undefined}
      onClose={isTemporary ? closeSidebar : undefined}
      className="sidebar-scrollbar"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          boxShadow: isDarkMode ? "2px 0 10px rgba(0, 0, 0, 0.3)" : "2px 0 5px rgba(0, 0, 0, 0.05)",
          backgroundColor: theme.palette.background.paper,
          overflowY: "auto",
          borderRight: isDarkMode ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      {/* Header with logo and close icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isTemporary ? "space-between" : "flex-start",
          height: 60,
          px: 2,
        }}
      >
        <img
          src={isDarkMode ? darklogo : Logo}
          alt="logo"
          style={{ width: "95px", height: "32px", cursor: "pointer" }}
        />
        {isTemporary && (
          <IconButton onClick={closeSidebar} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Divider />
      <List sx={{ width: "100%", p: 1, pt: 1.5 }}>
        {visibleItems.map((item) => {
          const isSelected = location.pathname === item.to;
          return (
            <ListItem disablePadding sx={{ mb: 0.5 }} key={item.label}>
              <ListItemButton
                component={Link}
                to={item.to}
                selected={isSelected}
                onClick={isTemporary ? closeSidebar : undefined}
                sx={{
                  py: 0.85,
                  px: 1.5,
                  borderRadius: "8px",
                  transition: "all 0.15s ease-in-out",
                  "&.Mui-selected": {
                    backgroundColor: activeBg,
                    "&:hover": {
                      backgroundColor: activeHoverBg,
                    },
                    "& .MuiListItemText-primary": {
                      color: activeColor,
                      fontWeight: 600,
                    },
                    "& .MuiListItemIcon-root": {
                      color: activeColor,
                    },
                  },
                  "&:hover": {
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 34,
                    color: isSelected ? activeColor : inactiveIconColor,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isSelected ? 600 : 500,
                    whiteSpace: "nowrap",
                    color: isSelected ? activeColor : inactiveTextColor,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
